// Destination fields for a Leads / Clients import.
//
// These keys ARE the backend contract — each one is a field on the canonical
// row serializers in `crm/imports/serializers.py`. The wizard maps a source
// column onto one of these, so the API never sees an export-specific header
// like `custom_Seeking_0`.
//
// `suggested_mappings` are matched case-insensitively against the file's own
// headers to pre-fill the mapping step. They include the legacy exporter's
// column names because that is the file this exists to import, but nothing
// downstream depends on them: an export with different headers just needs the
// user to pick the destination by hand.

const IDENTITY_FIELDS = (legacyIdKey, legacyIdLabel) => [
    {
        name: legacyIdLabel,
        key: legacyIdKey,
        required: true,
        suggested_mappings: [legacyIdKey],
        // The idempotency key: it is what stops a second upload of the same
        // file importing everything twice.
        help: 'Used to recognise rows that were already imported.',
    },
    { name: 'Name', key: 'name', required: true, suggested_mappings: ['name', 'Name', 'First Name', 'Last Name', 'Full Name'] },
    { name: 'Phone', key: 'phone', required: false, suggested_mappings: ['phone', 'Phone', 'Phone Number', 'Mobile'] },
    { name: 'Email', key: 'email', required: false, suggested_mappings: ['email', 'Email', 'Email Address'] },
    { name: 'Owner email', key: 'owner_email', required: false, suggested_mappings: ['owner_email', 'Owner Email'] },
    { name: 'Created at', key: 'created_at', required: false, suggested_mappings: ['created_at', 'Created At'] },
    { name: 'Updated at', key: 'updated_at', required: false, suggested_mappings: ['updated_at', 'Updated At'] },
    { name: 'Historical quotes', key: 'legacy_deal_ids', required: false, suggested_mappings: ['legacy_deal_ids'] },
]

export const IMPORT_TYPES = {
    leads: {
        type: 'leads',
        label: 'Leads',
        title: 'Import Leads',
        description: 'Upload a file of leads and review the data before importing it into the pipeline.',
        fields: [
            ...IDENTITY_FIELDS('legacy_lead_id', 'Legacy lead ID'),
            {
                name: 'Seeking',
                key: 'seeking',
                required: false,
                suggested_mappings: ['seeking', 'custom_Seeking_0', 'Seeking'],
            },
            {
                name: 'Trade-in vehicle',
                key: 'trade_in_vehicle',
                required: false,
                suggested_mappings: ['trade_in_vehicle', 'custom_Trade-in_Vehicle_1', 'Trade-in Vehicle'],
            },
        ],
    },
    clients: {
        type: 'clients',
        label: 'Clients',
        title: 'Import Clients',
        description: 'Upload a file of clients and review the data before importing it into the CRM.',
        fields: [
            ...IDENTITY_FIELDS('legacy_client_id', 'Legacy client ID'),
            { name: 'Inception date', key: 'inception_date', required: false, suggested_mappings: ['inception_date', 'Inception Date'] },
            { name: 'Maturity date', key: 'maturity_date', required: false, suggested_mappings: ['maturity_date', 'Maturity Date'] },
            {
                name: 'Dealership',
                key: 'dealership',
                required: false,
                // The real export carries two dealership columns whose meaning
                // was never settled, so neither is auto-mapped: the user picks
                // which one is the dealership rather than the two being merged.
                suggested_mappings: ['dealership', 'Dealership'],
            },
            { name: 'Trade-in profit', key: 'trade_in_profit', required: false, suggested_mappings: ['trade_in_profit', 'Trade In Profit'] },
            { name: 'Trade-in profit status', key: 'trade_in_profit_status', required: false, suggested_mappings: ['trade_in_profit_status'] },
            { name: 'Broker fee profit', key: 'broker_fee_profit', required: false, suggested_mappings: ['broker_fee_profit', 'Broker Fee Profit'] },
            { name: 'Broker fee profit status', key: 'broker_fee_profit_status', required: false, suggested_mappings: ['broker_fee_profit_status'] },
            { name: 'Other profit', key: 'other_profit', required: false, suggested_mappings: ['other_profit', 'Other Profit'] },
            { name: 'Other profit status', key: 'other_profit_status', required: false, suggested_mappings: ['other_profit_status'] },

            // The client's current vehicle. All optional: the export fills them
            // from the deal that priced the car where one exists, and the user
            // fills in the rest by hand. Year, make, model and trim become the
            // sale's vehicle name; the others are stored as they are.
            { name: 'Year', key: 'year', required: false, suggested_mappings: ['year', 'Year', 'vehicle_year', 'Vehicle Year'] },
            { name: 'Make', key: 'make', required: false, suggested_mappings: ['make', 'Make', 'vehicle_make', 'Vehicle Make'] },
            { name: 'Model', key: 'model', required: false, suggested_mappings: ['model', 'Model', 'vehicle_model', 'Vehicle Model'] },
            { name: 'Trim', key: 'trim', required: false, suggested_mappings: ['trim', 'Trim', 'vehicle_trim', 'Vehicle Trim'] },
            { name: 'VIN', key: 'vin', required: false, suggested_mappings: ['vin', 'VIN'] },
            { name: 'MSRP', key: 'msrp', required: false, suggested_mappings: ['msrp', 'MSRP'] },
            {
                name: 'Lease mileage allowance',
                key: 'mileage_allowance',
                required: false,
                // The annual allowance (7,500 / 10,000 / 12,000 / 15,000), not
                // the odometer. Plain "Mileage" is accepted because that is what
                // the old sheets call it, and it is the allowance they mean.
                suggested_mappings: [
                    'mileage_allowance', 'Mileage Allowance', 'Lease Mileage Allowance',
                    'lease_mileage', 'Lease Mileage', 'annual_mileage', 'Annual Mileage',
                    'mileage', 'Mileage',
                ],
            },
            { name: 'Monthly payment', key: 'monthly_payment', required: false, suggested_mappings: ['monthly_payment', 'Monthly Payment', 'payment', 'Payment'] },
        ],
    },
}

export const IMPORT_TYPE_KEYS = ['leads', 'clients']
